/**
 * TV Checkmark Logic - Americas Market Constants
 * 
 * Implements the business requirement where TV checkmarks in Americas markets
 * should be based on visible TV only (excluding compressed TV) to encourage
 * active team building rather than relying on inherited volume from compression.
 * 
 * Implementation Status: COMPLETED
 * - Dashboard: Americas markets use values.value >= 1000 (raw TV, excludes compression)
 * - Partners: All markets use uncompressedTv >= 1000 (already correct)
 * - Other markets: Continue using qualified field (includes compression)
 * 
 * Data Structure Definitions (from GLOSSARY.md):
 * - uncompressedTv: Raw team volume (excludes compressed volume)
 * - compressedTv: Adjusted team volume (includes compression from inactive downlines)
 * - values.value: Represents uncompressed TV in achievementsHistory API
 * - values.qualified: Uses backend logic that includes compressed TV
 * 
 * Ticket Requirements:
 * - Americas markets: PR, DO, US, CA, MX  
 * - Checkmark appears only when visible TV (uncompressed) ≥ 1000
 * - Other markets continue using existing qualified field
 * 
 * @author Generated for TV Checkmark Fix  
 * @since 2025-01-29
 */

/**
 * Americas markets where visible TV logic applies
 * Based on ticket requirements: PR, DO, US, CA, MX
 * 
 * Country codes:
 * - PR: Puerto Rico
 * - DO: Dominican Republic (corrected from DR)
 * - US: United States
 * - CA: Canada  
 * - MX: Mexico
 */
export const AMERICAS_MARKETS = ['PR', 'DO', 'US', 'CA', 'MX'];

/**
 * Checks if a given country code belongs to Americas markets 
 * where TV checkmark should use visible TV logic
 * 
 * @param {string} countryCode - Two letter country code (e.g., 'US', 'CA')
 * @returns {boolean} - True if country should use visible TV logic
 * 
 * @example
 * isAmericasMarket('US') // returns true
 * isAmericasMarket('CA') // returns true
 * isAmericasMarket('DO') // returns true (Dominican Republic)
 * isAmericasMarket('GB') // returns false
 */
export const isAmericasMarket = (countryCode) => {
  return AMERICAS_MARKETS.includes(countryCode);
};

/**
 * Gets the appropriate TV value for Partners screen checkmark logic
 * 
 * Partners screen uses member data with uncompressedTv field.
 * This field represents raw team volume (excludes compressed volume from inactive downlines).
 * 
 * @param {Object} member - Member/partner data object from Partners API
 * @param {number} [member.uncompressedTv] - Raw team volume (excludes compression)
 * @param {string} countryCode - User's country code (determines TV logic application)
 * @returns {number} - TV value to use for 1000 milestone checkmark
 * 
 * @example
 * getVisibleTvValue({ uncompressedTv: 1200 }, 'US') // returns 1200
 * getVisibleTvValue({ uncompressedTv: 800 }, 'DO') // returns 800
 */
export const getVisibleTvValue = (member, _countryCode) => {
  // All markets use uncompressed TV (raw volume, excludes compression)
  // This implements the Americas market requirement correctly
  // Logic is applied based on the logged-in user's country, not individual member countries
  return member?.uncompressedTv || 0;
}; 